import { useEffect, useRef, useState } from "react";
import Button from "../../common/Button";
import Input from "../../common/Input";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../../config";
import { object, string } from "yup";
import TextError from "../../common/TextError";
import RadioQuestion from "../homepage/RadioQuestions";

const emailRegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validationSchema = object({
  first_name: string().trim().required("Please enter your first name"),
  last_name: string().trim().required("Please enter your last name"),
  email: string()
    .trim()
    .matches(emailRegExp, "Please enter a valid email")
    .required("Please enter an email"),
  company: string().trim().required("Please enter your company name"),
  designation: string().trim().required("Please enter your job title"),
});

const FormZerto = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    company: "",
    designation: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsCheck, setTermsCheck] = useState(false);
  const [termsCheckError, setTermsCheckError] = useState(false);
  const [quizErrors, setQuizErrors] = useState({
    environment: "",
    confidence_under_pressure: "",
    recovery_test: "",
  });

  const [environmentRequirement, setEnvironmentRequirement] = useState("");
  const [confidence, setConfidence] = useState("");
  const [recoverTest, setRecoverTest] = useState("");

  // Define the options dynamically
  const environmentOptions = [
    {
      label: "On-prem (VMware / physical)",
      value: "On-prem (VMware / physical)",
    },
    {
      label: "Public cloud (Azure / AWS / GCP)",
      value: "Public cloud (Azure / AWS / GCP)",
    },
    {
      label: "SaaS (Microsoft 365 / Salesforce, etc.)",
      value: "SaaS (Microsoft 365 / Salesforce, etc.)",
    },
    { label: "A mix / hybrid", value: "A mix / hybrid" },
  ];
  const confidenceOptions = [
    { label: "Very confident", value: "Very confident" },
    { label: "Somewhat confident", value: "Somewhat confident" },
    { label: "Low confidence", value: "Low confidence" },
    { label: "Unsure", value: "Unsure" },
  ];
  const recoveryOptions = [
    { label: "Less than 3 months ago", value: "Less than 3 months ago" },
    { label: "3 - 12 months ago", value: "3 - 12 months ago" },
    { label: "12+ months ago", value: "12+ months ago" },
    { label: "Never / not sure", value: "Never / not sure" },
  ];

  const inputRefs = useRef([]);

  function handleInputChange(e) {
    const { name, value } = e.target;

    setFormData((prevData) => {
      return { ...prevData, [name]: value };
    });
  }

  function handleTermsCheck(e) {
    setTermsCheckError(!e.target.checked);
    setTermsCheck(e.target.checked);
  }

  function clearFormData() {
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      company: "",
      designation: "",
    });
    setTermsCheck(false);
    setTermsCheckError(false);
    setEnvironmentRequirement("");
    setConfidence("");
    setRecoverTest("");
  }

  function showErrors(errors) {
    const newErrors = {};
    errors.forEach((error) => {
      newErrors[error.path] = error.message;
    });
    setErrors(newErrors);
  }

  async function handleFormSubmit(e) {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});

      // Validate that all quiz questions are answered first
      const newQuizErrors = { ...quizErrors };
      let hasQuizErrors = false;

      if (!environmentRequirement) {
        newQuizErrors.environment = "Please select an answer";
        hasQuizErrors = true;
      }
      if (!confidence) {
        newQuizErrors.confidence_under_pressure = "Please select an answer";
        hasQuizErrors = true;
      }
      if (!recoverTest) {
        newQuizErrors.recovery_test = "Please select an answer";
        hasQuizErrors = true;
      }

      if (hasQuizErrors) {
        setQuizErrors(newQuizErrors);
        console.log("Please answer all required questions");
        setIsSubmitting(false);
        return;
      }

      // Then check terms and conditions
      if (!termsCheck) {
        console.log("Please accept terms and conditions");
        setTermsCheckError(true);
        setIsSubmitting(false);
        return;
      }

      // Clear quiz errors on successful validation
      setQuizErrors({
        environment: "",
        confidence_under_pressure: "",
        recovery_test: "",
      });

      // Get UTM parameters from URL
      const params = new URLSearchParams(window.location.search);
      const utm_source = params.get("utm_source") || "";
      const utm_medium = params.get("utm_medium") || "";
      const utm_campaign = params.get("utm_campaign") || "";

      // Submit form to API
      await axios.post("/zerto/submit-form", {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        company: formData.company,
        designation: formData.designation,
        environment: environmentRequirement,
        confidence_under_pressure: confidence,
        recovery_test: recoverTest,
        utm_source,
        utm_medium,
        utm_campaign,
      });

      const pdfPath =
        "/pdfs/zerto/understanding-real-time-encryption-detection.pdf";

      const link = document.createElement("a");
      link.href = pdfPath;
      link.setAttribute(
        "download",
        "understanding-real-time-encryption-detection.pdf",
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Navigate after a short delay to ensure download starts
      setTimeout(() => {
        navigate("/hpe-zerto-software/thank-you");
        clearFormData();
      }, 500);
    } catch (error) {
      if (error.inner) {
        console.log("validation errors: ", error.inner);
        showErrors(error.inner);
      } else {
        console.log("Error: ", error);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  useEffect(() => {
    const invalidInputFieldNames = Object.keys(errors).filter(
      (key) => errors[key],
    );

    const invalidInputIndex = inputRefs.current.findIndex(
      (input) => input?.name === invalidInputFieldNames[0],
    );

    if (invalidInputIndex !== -1) {
      inputRefs.current[invalidInputIndex]?.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });

      setTimeout(() => {
        inputRefs.current[invalidInputIndex]?.focus();
      }, 500);
    }
  }, [errors]);

  useEffect(() => {
    if (location.state) {
      setFormData((prevData) => {
        return { ...prevData, ...location.state };
      });
    }
    window.scrollTo(0, 0);
  }, [location.state]);

  return (
    <div className="w-full lg:w-[45%] px-4 md:px-10 py-8 bg-white rounded-lg shadow-header mb-[52px] lg:mb-32 z-20 relative">
      <div className="flex flex-col gap-3 mb-7">
        <h3 className="!font-semibold text-black">
          Download the Technical White paper
        </h3>

        <p className="b2 text-black">
          Understanding real-time encryption detection with HPE Zerto Software
        </p>

        <p className="b3 text-grey-05">
          Technical White paper | PDF | 511 KB | 11 pages
        </p>
      </div>

      <p className="text-black h4 !text-[16px] md:!text-[24px] !font-normal mb-6 md:mb-8">
        Complete this form to access: Technical White paper
      </p>
      {/* <div className="flex items-center gap-1 mb-6 md:mb-8">
        <ProgressStep className="bg-blue-06" />
        <ProgressStep className="bg-blue-06" />
      </div> */}

      <h4 className="text-grey-06 !font-normal mb-4">Your Details</h4>

      <form onSubmit={handleFormSubmit} className="flex flex-col gap-8">
        <div className="flex flex-col gap-8">
          <Input
            placeholder="Enter your name"
            label="First name*"
            name="first_name"
            value={formData.first_name}
            onChange={handleInputChange}
            error={errors.first_name}
            ref={(node) => (inputRefs.current[0] = node)}
          />
          <Input
            placeholder="Enter your last name"
            label="Last name*"
            name="last_name"
            value={formData.last_name}
            onChange={handleInputChange}
            error={errors.last_name}
            ref={(node) => (inputRefs.current[1] = node)}
          />
          <Input
            placeholder="Enter your work email"
            label="Work email*"
            name="email"
            // type="email"
            value={formData.email}
            onChange={handleInputChange}
            error={errors.email}
            ref={(node) => (inputRefs.current[2] = node)}
          />
          <Input
            placeholder="Enter your designation"
            label="Job title*"
            name="designation"
            value={formData.designation}
            onChange={handleInputChange}
            error={errors.designation}
            ref={(node) => (inputRefs.current[3] = node)}
          />
          <Input
            placeholder="Enter your company name"
            label="Company name*"
            name="company"
            value={formData.company}
            onChange={handleInputChange}
            error={errors.company}
            ref={(node) => (inputRefs.current[4] = node)}
          />
        </div>

        <hr className="h-px bg-grey-01 my-2" />

        <section className="flex flex-col gap-4">
          <h4 className="text-grey-06 !font-normal">Resilience Quick-Check</h4>
          <div className="flex flex-col gap-8">
            <div>
              <RadioQuestion
                question="Which environment is hardest for you to recover today?*"
                name="environment_requirement"
                options={environmentOptions}
                selectedValue={environmentRequirement}
                onChange={(value) => {
                  setEnvironmentRequirement(value);
                  setQuizErrors((prev) => ({
                    ...prev,
                    environment: "",
                  }));
                }}
              />
              {quizErrors.environment && (
                <TextError error={quizErrors.environment} />
              )}
            </div>
            <div>
              <RadioQuestion
                question="How confident are you that you can restore clean systems (not reinfected) under pressure?*"
                name="confidence"
                options={confidenceOptions}
                selectedValue={confidence}
                onChange={(value) => {
                  setConfidence(value);
                  setQuizErrors((prev) => ({
                    ...prev,
                    confidence_under_pressure: "",
                  }));
                }}
              />
              {quizErrors.confidence_under_pressure && (
                <TextError error={quizErrors.confidence_under_pressure} />
              )}
            </div>

            <div>
              <RadioQuestion
                question="When was your last end-to-end recovery test of a critical application?*"
                name="recovery"
                options={recoveryOptions}
                selectedValue={recoverTest}
                onChange={(value) => {
                  setRecoverTest(value);
                  setQuizErrors((prev) => ({
                    ...prev,
                    recovery_test: "",
                  }));
                }}
              />
              {quizErrors.recovery_test && (
                <TextError error={quizErrors.recovery_test} />
              )}
            </div>
          </div>
        </section>
        <div>
          <div className="flex gap-4 items-baseline">
            <input
              type="checkbox"
              id="terms"
              name="terms"
              onChange={handleTermsCheck}
              checked={termsCheck}
            />
            <label htmlFor="terms" className="b3 text-grey-05">
              I have read and agree to{" "}
              <a className="text-purple-06" href="/terms">
                Terms of use
              </a>{" "}
              and understand that my personal information will be processed in
              accordance with{" "}
              <a className="text-purple-06" href="/privacy-policy">
                Privacy policy
              </a>
              .
            </label>
          </div>
          {termsCheckError && (
            <TextError error="Please mark the above checkbox" />
          )}
        </div>

        <p className="text-grey-05 b3">
          By supplying my contact information, I authorise{" "}
          <a href="http://computechoice.com/" className="underline">
            computechoice.com
          </a>{" "}
          to contact me with personalized communication about{" "}
          <a href="http://computechoice.com/" className="underline">
            computechoice.com
          </a>{" "}
          products and services. See our Privacy Policy link (above) for more
          details or to optout at any time.
        </p>

        {/* <div className="b3 text-grey-05">
          By supplying my contact information, I authorise Campaigns.theone.cx to contact me with personalized communication about Campaigns.theone.cx.com products and services. See our Privacy Policy link (above) for more details or to opt-out at any time.
        </div> */}
        <Button
          text={!isSubmitting ? "Download the Report" : "Downloading..."}
          disabled={isSubmitting}
          styles="!text-[18px]"
        />
      </form>
    </div>
  );
};
export default FormZerto;
